// import { BeakerIcon } from "@heroicons/react/24/solid";
import type { ReactNode } from "react";
import { tinaField, useTina } from "tinacms/dist/react";
import Hero from "../ui-elements/Hero";
import MainBlock from "../ui-elements/MainBlock";
import BoxInBoxTile from "../ui-elements/BoxInBoxTile";
import MainTitle from "../ui-elements/MainTitle";
import FeatureCompearisonPriceTable from "../ui-elements/FeatureCompearisonPriceTable";
import Gallery from "../ui-elements/Gallery";
import homeContent from "../../../content/pages/home.json";
import { PageDocument } from "../../../tina/__generated__/types";
import { PageSeo } from "../../navigation/PageSeo";

// Must be a stable reference: useTina re-syncs whenever the data object identity changes.
const initialHomeData = { page: homeContent };
const homeQueryVariables = { relativePath: "home.json" };

// Wraps each highlight phrase found in the title with the highlightedText style.
const renderHighlightedTitle = (
  title: string,
  highlights: Array<string | null | undefined>,
  highlightField?: (index: number) => string | undefined
): ReactNode => {
  const phrases = highlights
    .map((phrase, index) => ({ phrase, index }))
    .filter((entry): entry is { phrase: string; index: number } => Boolean(entry.phrase));
  if (phrases.length === 0) return title;
  const escaped = phrases.map(({ phrase }) => phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = title.split(new RegExp(`(${escaped.join("|")})`, "g"));
  return parts.map((part, partIndex) => {
    const phrase = phrases.find((entry) => entry.phrase === part);
    return phrase ? (
      <span key={partIndex} className="highlightedText" data-tina-field={highlightField?.(phrase.index)}>
        {part}
      </span>
    ) : (
      part
    );
  });
};

export const Home = () => {
  // Outside the Tina editor this returns the static JSON; inside it streams unsaved form edits.
  const { data } = useTina({
    query: PageDocument,
    variables: homeQueryVariables,
    data: initialHomeData,
  });
  const home = data.page;
  const { hero, intro, benefits, pricing, tiles, reviews } = home;
  return (
    <>
      <main className="mx-0  bg-light-ui dark:bg-dark-purple-ui">
        <PageSeo pathname="/" seo={home.seo} />
        <Hero
          {...hero}
          tinaFields={{
            title: tinaField(hero, "title"),
            highlight: tinaField(hero, "highlight"),
            subtitle: tinaField(hero, "subtitle"),
            ctaLabel: tinaField(hero, "ctaLabel"),
            ctaHref: tinaField(hero, "ctaHref"),
            imageSrc: tinaField(hero, "imageSrc"),
            imageAlt: tinaField(hero, "imageAlt"),
          }}
        />
        <MainTitle
          label={intro.label}
          title={
            <>
              {renderHighlightedTitle(intro.title, intro.highlights ?? [], (index) =>
                tinaField(intro, "highlights", index)
              )}
            </>
          }
          description={intro.description}
          fieldBindings={{
            label: tinaField(intro, "label"),
            title: tinaField(intro, "title"),
            description: tinaField(intro, "description"),
          }}
        ></MainTitle>
        <MainBlock benefits={benefits} />
        <MainTitle label="" title={""} description=""></MainTitle>
        <FeatureCompearisonPriceTable pricing={pricing} />
        <div className="cardsList mx-auto grid max-w-7xl grid-cols-1 items-stretch justify-items-center gap-14 px-6 py-10 md:grid-cols-2 md:gap-8 lg:grid-cols-4 lg:gap-10">
          {(tiles ?? []).map((tile, index) => (
            <BoxInBoxTile
              key={`${tile.title}-${index}`}
              {...tile}
              fieldBindings={{
                title: tinaField(tile, "title"),
                description: tinaField(tile, "description"),
                href: tinaField(tile, "href"),
                linkLabel: tinaField(tile, "linkLabel"),
                imageSrc: tinaField(tile, "imageSrc"),
                imageAlt: tinaField(tile, "imageAlt"),
              }}
            />
          ))}
        </div>
        <Gallery />
        <section className="mx-auto mt-16 mb-0 max-w-4xl px-6 pb-16" aria-labelledby="google-reviews-title">
          <div className="rounded-3xl bg-brand-purple-secondary-light px-6 py-10 text-center shadow-sm dark:bg-[#2b1742] sm:px-10">
            <p className="eyebrow" data-tina-field={tinaField(reviews, "eyebrow")}>
              {reviews.eyebrow}
            </p>
            <h2 id="google-reviews-title" className="section-title" data-tina-field={tinaField(reviews, "title")}>
              {reviews.title}
            </h2>
            <p
              className="mx-auto mt-[0.8rem] text-[0.95rem] leading-[1.7] text-slate-600 dark:text-slate-300"
              data-tina-field={tinaField(reviews, "description")}
            >
              {reviews.description}
            </p>
            <div
              className="mt-6 flex flex-col items-center justify-center gap-1 sm:flex-row sm:gap-3"
              aria-label={`Rated ${reviews.rating} out of 5 ${reviews.ratingSummary}`}
            >
              <span aria-hidden="true" className="text-2xl tracking-widest text-amber-500">
                ★★★★★
              </span>
              <span
                className="text-lg font-semibold text-brand-purple-dark dark:text-white"
                data-tina-field={tinaField(reviews, "rating")}
              >
                {reviews.rating}
              </span>
              <span
                className="text-slate-600 dark:text-slate-300"
                data-tina-field={tinaField(reviews, "ratingSummary")}
              >
                {reviews.ratingSummary}
              </span>
            </div>
            <div className="mt-8 grid gap-4 text-left md:grid-cols-3">
              {(reviews.items ?? []).map((review, index) => (
                <article
                  key={`${review.author}-${index}`}
                  className="rounded-2xl bg-white p-5 shadow-sm dark:bg-[#38244d]"
                  data-tina-field={tinaField(review)}
                >
                  <p className="text-lg tracking-widest text-amber-500" aria-label="5 out of 5 stars">
                    ★★★★★
                  </p>
                  <p className="mt-3 text-slate-700 dark:text-slate-200" data-tina-field={tinaField(review, "text")}>
                    {review.text}
                  </p>
                  <p
                    className="mt-4 text-sm font-semibold text-brand-purple-dark dark:text-white"
                    data-tina-field={tinaField(review, "author")}
                  >
                    {review.author}
                  </p>
                </article>
              ))}
            </div>
            <p
              className="mt-5 text-xs text-slate-500 dark:text-slate-400"
              data-tina-field={tinaField(reviews, "disclaimer")}
            >
              {reviews.disclaimer}
            </p>
            <a
              className="mt-6 inline-flex rounded-full bg-brand-purple-main px-6 py-3 font-semibold text-white transition-colors hover:bg-brand-purple-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-purple-main"
              href={reviews.ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              data-tina-field={tinaField(reviews, "ctaHref")}
            >
              <span data-tina-field={tinaField(reviews, "ctaLabel")}>{reviews.ctaLabel}</span>
            </a>
          </div>
        </section>
        {/* <div className=" h-[calc(100vh-4em)] flex flex-col justify-center items-center">
          <h1 className="text-5xl font-bold text-center text-gray-800 dark:text-gray-200">
            Home
          </h1>
          <BeakerIcon className="w-20 h-20 mt-4 text-gray-800 dark:text-gray-200" />
        </div> */}
      </main>
    </>
  );
};
